import type { OrderTicket } from '../lib/catalog';
import { formatMoney, shortId } from '../lib/format';
import './TicketQr.css';

/**
 * One ticket.
 *
 * The QR arrives from the engine as a data: URL and is rendered directly — it is
 * never cached or persisted here. A ticket without a QR is one the payment
 * webhook has not activated yet, and it renders as a placeholder rather than a
 * broken image.
 */
export function TicketQr({ ticket, index }: { ticket: OrderTicket; index: number }) {
  const ready = ticket.status === 'active' && ticket.qr != null;

  return (
    <figure className={`ticket${ready ? '' : ' ticket--pending'}`}>
      <div className="ticket__qr">
        {ready ? (
          <img src={ticket.qr!} alt={`QR code for ticket ${index}`} width={220} height={220} />
        ) : (
          <div className="ticket__qr-placeholder" aria-hidden="true">
            <span className="ticket__qr-spinner" />
          </div>
        )}
      </div>

      <figcaption className="ticket__body">
        <span className="ticket__tier">{ticket.tierName ?? 'General admission'}</span>
        <span className="ticket__id mono" title={ticket.ticketUid}>
          {shortId(ticket.ticketUid)}
        </span>
        {ticket.priceCents != null && (
          <span className="ticket__price mono">{formatMoney(ticket.priceCents)}</span>
        )}
        <span className={`ticket__status ticket__status--${ready ? 'active' : 'pending'}`}>
          {ready ? 'Valid for entry' : 'Awaiting confirmation'}
        </span>
      </figcaption>

      {ready && (
        <a
          className="button button--ghost ticket__download"
          href={ticket.qr!}
          download={`voleto-ticket-${shortId(ticket.ticketUid)}.png`}
        >
          Download
        </a>
      )}
    </figure>
  );
}
